import React, { useEffect, useRef, useState, useMemo } from "react";
import { DrawingManager, Polygon, useGoogleMap } from "@react-google-maps/api";
import { Icon } from "@iconify/react";

/* ================= HELPERS ================= */
const normalizePolygons = (data) => {
  if (!data) return [];
  let parsed;
  try { 
    parsed = typeof data === "string" ? JSON.parse(data) : data; 
  } catch { 
    return []; 
  }
  
  const sanitizePoint = (pt) => ({
    lat: Number(pt.lat) || 0,
    lng: Number(pt.lng) || 0
  });

  // If it's the structure: [{path: [...]}, {path: [...]}]
  if (Array.isArray(parsed) && parsed.length && parsed[0].path) {
    return parsed.map(p => (p.path || []).map(sanitizePoint));
  }
  // If it's already an array of arrays: [[{lat,lng}]]
  if (Array.isArray(parsed) && parsed.every(p => Array.isArray(p))) {
    return parsed.filter(p => p.length).map(poly => poly.map(sanitizePoint));
  }
  // If it's a single array: [{lat,lng}]
  if (Array.isArray(parsed) && parsed.length && parsed[0].lat !== undefined) {
    return [parsed.map(sanitizePoint)];
  }
  return [];
};

const polygonToPath = (polygon) =>
  polygon.getPath().getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));

const THEME_COLORS = {
  unselectedFill: "#0d6efd",     // Blue
  unselectedStroke: "#0b5ed7",   // Darker blue
  selectedFill: "#f97316",       // Orange
  selectedStroke: "#ea580c",     // Darker orange
  fillOpacityUnselected: 0.30,
  fillOpacitySelected: 0.45,
  strokeWeightUnselected: 2.5,
  strokeWeightSelected: 3.5
};

const DrawTools = ({ data, onChange, onVertexSelect, activePolyIndex = 0 }) => {
  const map = useGoogleMap();
  const [polygons, setPolygons] = useState([]);
  const [selectedPathIndex, setSelectedPathIndex] = useState(0);
  const [isLibLoaded, setIsLibLoaded] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const drawingManagerRef = useRef(null);
  const polygonRefs = useRef([]);
  const isInternalUpdate = useRef(false);

  // Sync selected shape when activePolyIndex changes from listview
  useEffect(() => {
    if (typeof activePolyIndex === "number" && activePolyIndex >= 0) {
      setSelectedPathIndex(activePolyIndex);
    }
  }, [activePolyIndex]);

  // 1. Library Detection
  useEffect(() => {
    const checkDrawingLib = setInterval(() => {
      if (window.google?.maps?.drawing) {
        setIsLibLoaded(true);
        clearInterval(checkDrawingLib);
      }
    }, 100);
    return () => clearInterval(checkDrawingLib);
  }, []);

  // Helper to bind all path and click listeners to a Polygon instance
  const bindPolygonListeners = (poly, polyIdx) => {
    if (!poly) return;

    // Clean up previous listeners attached to this poly
    if (poly._customListeners) {
      poly._customListeners.forEach((l) => window.google?.maps?.event?.removeListener(l));
    }
    poly._customListeners = [];

    const attachToPath = () => {
      const path = poly.getPath();
      if (!path) return;

      const notifyChange = (eventType, vertexIdx) => {
        isInternalUpdate.current = true;
        const currentCoords = polygonToPath(poly);

        setPolygons((prev) => {
          const next = [...prev];
          next[polyIdx] = currentCoords;
          return next;
        });

        // Collect current state from all active polygons
        const allPaths = (polygonRefs.current || [])
          .map((p, i) => {
            if (!p) return null;
            if (i === polyIdx) return currentCoords;
            return polygonToPath(p);
          })
          .filter(Boolean);

        const out = allPaths.length > 0 ? allPaths : [currentCoords];
        onChange(JSON.stringify(out));

        if (typeof vertexIdx === "number" && onVertexSelect) {
          onVertexSelect(polyIdx, vertexIdx);
        }
      };

      const l1 = window.google.maps.event.addListener(path, "set_at", (idx) => notifyChange("set_at", idx));
      const l2 = window.google.maps.event.addListener(path, "insert_at", (idx) => notifyChange("insert_at", idx));
      const l3 = window.google.maps.event.addListener(path, "remove_at", (idx) => notifyChange("remove_at", idx));
      poly._customListeners.push(l1, l2, l3);
    };

    // Attach to current path MVCArray
    attachToPath();

    // Listen to getPaths() in case the array itself is replaced
    const pathsArray = poly.getPaths();
    if (pathsArray) {
      const lPaths = window.google.maps.event.addListener(pathsArray, "set_at", () => {
        attachToPath();
      });
      poly._customListeners.push(lPaths);
    }

    // Detect click on polygon and select closest vertex
    const clickListener = window.google.maps.event.addListener(poly, "click", (e) => {
      setSelectedPathIndex(polyIdx);
      if (e?.latLng) {
        const cLat = e.latLng.lat();
        const cLng = e.latLng.lng();
        const path = poly.getPath();
        if (path) {
          let closestIdx = 0;
          let minD = Infinity;
          path.getArray().forEach((pt, pIdx) => {
            const dLat = pt.lat() - cLat;
            const dLng = pt.lng() - cLng;
            const dist = dLat * dLat + dLng * dLng;
            if (dist < minD) {
              minD = dist;
              closestIdx = pIdx;
            }
          });
          if (onVertexSelect) {
            onVertexSelect(polyIdx, closestIdx);
          }
        }
      }
    });
    poly._customListeners.push(clickListener);
  };

  // 2. Data Load & Sync with Form (External Updates from Table or Form)
  useEffect(() => {
    // If change originated from dragging on map, skip setPaths to keep map smooth and listeners intact
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (data) {
      const normalized = normalizePolygons(data);
      const currentStr = JSON.stringify(polygons);
      const incomingStr = JSON.stringify(normalized);
      if (currentStr !== incomingStr) {
        setPolygons(normalized);
        normalized.forEach((p, idx) => {
          const polyInstance = polygonRefs.current[idx];
          if (polyInstance && typeof polyInstance.setPaths === 'function') {
            polyInstance.setPaths(p);
            bindPolygonListeners(polyInstance, idx);
          }
        });
      }
    } else {
      if (polygons.length > 0) {
        setPolygons([]);
        polygonRefs.current = [];
      }
    }
  }, [data]);

  const syncChanges = (updatedList) => {
    isInternalUpdate.current = true;
    setPolygons(updatedList);
    onChange(JSON.stringify(updatedList));
  };

  const startDrawing = () => {
    if (drawingManagerRef.current && window.google?.maps?.drawing) {
      drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
      setIsDrawing(true);
    }
  };

  const cancelDrawing = () => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
      setIsDrawing(false);
    }
  };

  const onPolygonComplete = (polygon) => {
    const newPath = polygonToPath(polygon);
    polygon.setMap(null); 
    const updated = [...polygons, newPath];
    setSelectedPathIndex(updated.length - 1);
    setIsDrawing(false);
    syncChanges(updated);

    if (onVertexSelect) {
      onVertexSelect(updated.length - 1, 0);
    }
    
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
    }
  };

  const drawingManagerOptions = useMemo(() => ({
    drawingControl: true,
    drawingControlOptions: {
      position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
      drawingModes: ["polygon"]
    },
    polygonOptions: {
      fillColor: THEME_COLORS.selectedFill,
      fillOpacity: THEME_COLORS.fillOpacitySelected,
      strokeColor: THEME_COLORS.selectedStroke,
      strokeWeight: THEME_COLORS.strokeWeightSelected,
      editable: true,
      zIndex: 25
    }
  }), []);

  if (!isLibLoaded || !map) return null;

  return (
    <>
      {/* TOOLBAR */}
      <div className="position-absolute top-0 start-50 d-flex align-items-center gap-2" 
           style={{ zIndex: 1000, marginTop: '10px', transform: 'translateX(-50%)' }}>
        {!isDrawing ? (
          <button 
            type="button" 
            className="btn btn-xs bg-white text-dark radius-6 border shadow-sm d-flex align-items-center gap-1 px-10 py-6 text-xs fw-semibold"
            onClick={startDrawing}
            title="Draw an additional shape on the map"
          >
            <Icon icon="solar:pen-new-square-bold" className="text-primary-600 text-sm" />
            <span>+ Draw Shape</span>
          </button>
        ) : (
          <div className="d-flex align-items-center gap-2 bg-white px-10 py-4 radius-6 shadow-sm border">
            <span className="badge bg-warning-subtle text-warning-800 radius-4 px-6 py-2 text-xs fw-medium d-flex align-items-center gap-1">
              <Icon icon="solar:danger-circle-linear" />
              Click map to place points, close shape to complete
            </span>
            <button
              type="button"
              className="btn btn-xs btn-outline-secondary radius-4 py-0 px-6 text-xs"
              onClick={cancelDrawing}
            >
              Cancel
            </button>
          </div>
        )}

        {selectedPathIndex !== null && polygons.length > 1 && !isDrawing && (
          <button 
            type="button" 
            className="btn btn-xs bg-danger text-white radius-6 border-0 shadow-sm d-flex align-items-center gap-1 px-10 py-6 text-xs fw-medium"
            onClick={() => {
              const updated = polygons.filter((_, i) => i !== selectedPathIndex);
              setSelectedPathIndex(0);
              syncChanges(updated);
              if (onVertexSelect) {
                onVertexSelect(0, 0);
              }
            }}
            title="Delete currently selected shape"
          >
            <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "13px" }} />
            <span>Delete Shape {selectedPathIndex + 1}</span>
          </button>
        )}
      </div>

      <DrawingManager
        onLoad={(dm) => { drawingManagerRef.current = dm; }}
        onPolygonComplete={onPolygonComplete}
        options={drawingManagerOptions}
      />

      {polygons.map((path, index) => {
        const isSelected = selectedPathIndex === index;
        return (
          <Polygon
            key={`poly-${index}`}
            paths={path}
            onLoad={(poly) => {
              polygonRefs.current[index] = poly;
              bindPolygonListeners(poly, index);
            }}
            onUnmount={() => {
              if (polygonRefs.current[index]?._customListeners) {
                polygonRefs.current[index]._customListeners.forEach((l) =>
                  window.google?.maps?.event?.removeListener(l)
                );
              }
              delete polygonRefs.current[index];
            }}
            options={{
              fillColor: isSelected ? THEME_COLORS.selectedFill : THEME_COLORS.unselectedFill,
              fillOpacity: isSelected ? THEME_COLORS.fillOpacitySelected : THEME_COLORS.fillOpacityUnselected,
              strokeColor: isSelected ? THEME_COLORS.selectedStroke : THEME_COLORS.unselectedStroke,
              strokeWeight: isSelected ? THEME_COLORS.strokeWeightSelected : THEME_COLORS.strokeWeightUnselected,
              editable: isSelected,
              draggable: false,
              zIndex: isSelected ? 20 : 10
            }}
          />
        );
      })}
    </>
  );
};

export default DrawTools;